
import { createSelector } from 'reselect';
import { Map as ImmutableMap, OrderedMap } from 'immutable';
import { DateTime } from 'luxon';
import store from 'store';

import { getLogger } from 'companion-app-components/utils/core';
import { accountsSelectors, accountsUtils } from 'companion-app-components/flux/accounts';
import { ShowBalance } from 'companion-app-components/flux/preferences/types';
import { getSharedPreferencesByPath } from 'companion-app-components/flux/preferences/selectors';
import { getIHoldsByAccountId } from 'companion-app-components/flux/investmentHoldings/selectors';
import { featureFlagsSelectors } from 'companion-app-components/flux/feature-flags';

import { getRunningBalanceExcludeAccountIds } from 'data/preferences/selectors';
import { getTransactionsStore, getTransactionsByAccountId, getIsLoading as transactionsLoading, TxnTypes } from 'data/transactions/selectors';
import { isUnacceptedScheduledTxn, nextScheduledInstanceAfterDate, isBankOwnedPending, isC2RTransaction } from 'data/transactions/utils';
import { getOptimalCacheFromObject, setOptimalCacheFromObject } from 'data/optimalCacheManager';

import { noNaN } from 'utils/utils';
import { memoizeWithKey } from 'utils/memoizeWithKey';

import { AccountBalances } from './types';

import { getBalMap } from './balCache';

const log = getLogger('data/accountBalances/selectors');

export function isConnectedAccount(account) {
  return account.institutionLoginId &&
    account.aggregators && account.aggregators.size > 0;
}

export function includeRecurringTxn(txn, recurringSetting, transactionsForAccountId) {

  // "next scheduled instance", but this needs to be the next scheduled instance after TODAY
  // so it still shows all prior instances
  
  const txnDate = DateTime.fromISO(txn.postedOn);
  const currentDate = DateTime.local();
  const sanitizeNaN = noNaN(recurringSetting);

  if (sanitizeNaN === -1) {
    // include all due and overdue
    const isSameOrBefore = txnDate <= currentDate || txnDate.hasSame(currentDate, 'day');
    if (isSameOrBefore) {
      return true;
    }
    const nextInstanceAfterToday = nextScheduledInstanceAfterDate(currentDate, transactionsForAccountId, txn.stModelId);
    return nextInstanceAfterToday && (txn.id === nextInstanceAfterToday.id);
  }
  if (sanitizeNaN === 0) {
    return false;
  }

  const mostRecentSchedTxnDate = currentDate.plus({ days: sanitizeNaN });
  return txnDate.hasSame(mostRecentSchedTxnDate, 'day') || txnDate <= mostRecentSchedTxnDate;
}

function includeTransactionInBalance(
  txn,
  {
    recurringSetting,
    transactionsForAccountId,
    excludePendingTxn,
    isPendingTxnDisabled,
  },
) {
  if ((isPendingTxnDisabled || excludePendingTxn) && isBankOwnedPending(txn)) {
    return false;
  }

  if (isUnacceptedScheduledTxn(txn)) {
    return includeRecurringTxn(txn, recurringSetting, transactionsForAccountId);
  }

  return !isC2RTransaction(txn);
}

function didBalanceCacheBreak(cacheRec, props) {
  const { acctReminderSetting, account, txns } = props;
  return (!cacheRec) ||
  (cacheRec.txns !== txns) ||
  (cacheRec.acctReminderSetting !== acctReminderSetting) ||
  (cacheRec.account.currentBalanceAsOf !== account.currentBalanceAsOf) ||
  (cacheRec.account.onlineBalance !== account.onlineBalance) ||
  (cacheRec.account.balanceAsOf !== account.balanceAsOf) ||
  (cacheRec.account.currentBalanceAsOfOn !== account.currentBalanceAsOfOn) ||
  (cacheRec.account.balanceAsOfOn !== account.balanceAsOfOn) ||
  (cacheRec.account.aggregators?.first?.()?.onlineBalance !== account.aggregators?.first?.()?.onlineBalance) ||
  (cacheRec.account.aggregators?.first?.()?.onlineBalanceAt !== account.aggregators?.first?.()?.onlineBalanceAt) ||
  (cacheRec.account.aggregators?.first?.()?.availableBalance !== account.aggregators?.first?.()?.availableBalance);
}

function createNewBalancesRecord(cacheRec, props) {

  const { account, isConnected } = props;
  const endBal = cacheRec ? cacheRec.endingBalance : 0;
  const endBalNoRecurring = cacheRec ? cacheRec.endingBalanceNoRecurring : 0;
  const currBal = cacheRec ? cacheRec.currentBalance : 0;
  const finalBal = cacheRec ? cacheRec.finalBalance : 0;
  const txnsDeltaFromCurrentBalance = cacheRec ? cacheRec.txnsDeltaFromCurrentBalance : 0;
  const eDate = cacheRec ? cacheRec.earliestTransactionDate : null;
  return new AccountBalances({
    accountId: account.id,
    accountType: account.type,
    endingBalance: endBal,
    endingBalanceNoRecurring: endBalNoRecurring,
    onlineBalance: !isConnected ? currBal : account.onlineBalance,
    currentBalance: currBal,
    finalBalance: finalBal,
    earliestTransactionDate: eDate,
    txnsDeltaFromCurrentBalance,
    cpOnlineBalance: cacheRec?.cpOnlineBalance,
    cpAvailableBalance: cacheRec?.cpAvailableBalance,
  });
}

const balRound = (val) => val && Number(val.toFixed(2));

const optimalCacheCompareFn = (oldTxn, newTxn, accountId) => {

  // if this txn change does not involve this account, then no recalc needed
  if (oldTxn.accountId !== accountId && newTxn.accountId !== accountId) {
    return true;
  }

  // if there is no oldTxn (add), then return false
  if (!oldTxn) return false;
  //
  // Need to return false for all cases in which a change in a transaction field can cause a balance to change
  //
  if (newTxn.accountId !== oldTxn.accountId) return false;
  if (newTxn.isDeleted) return false;
  // did we move forward or backwards relative to today?

  const currentDate = DateTime.local();
  const oldTxnDate = DateTime.fromISO(oldTxn.postedOn);
  const newTxnDate = DateTime.fromISO(newTxn.postedOn);

  if ((oldTxnDate <= currentDate && newTxnDate > currentDate) || (newTxnDate <= currentDate && oldTxnDate > currentDate)) {
    return false;
  }
  return (oldTxn.amount === newTxn.amount && oldTxn.postedOn === newTxn.postedOn);
};

export const getBalancesByAccountId = createSelector(
  accountsSelectors.getAccountsByIdIncludingGoals,
  (state) => getTransactionsByAccountId(state, { txnTypes: TxnTypes.REGULAR_TXNS | TxnTypes.UNACCEPTED_SCHEDULED_TXNS }),
  transactionsLoading,
  accountsSelectors.getIsLoading,
  getTransactionsStore,
  getRunningBalanceExcludeAccountIds,

  ((function combinerClosure() {

    let overallMapCache;

    return ((accounts, transactionsByAccountId, transactionsAreLoading,
      accountsAreLoading, transactionsStore, runningBalanceExcludeAccountIds) => {

      let atLeastOneAccountBrokeCache = false;
      const retVal = (ImmutableMap()).withMutations((balancesByAccountId) => {

        const balMap = getBalMap();

        accounts.forEach((account) => {
          if (account.type === 'INVESTMENT') return;

          const excludePendingTxn = runningBalanceExcludeAccountIds?.includes(account.id);

          const isConnected = isConnectedAccount(account);

          const txns = transactionsByAccountId.get(account.id);

          let acctReminderSetting = 0;
          acctReminderSetting = accountsUtils.getReminderSettingInDays(account.recurringTxn);

          /*
           * PER ACCOUNT CACHEING
           */
          const cacheKey = account.id;
          const cacheRec = balMap[cacheKey];
          const cacheBroke = didBalanceCacheBreak(cacheRec, {
            acctReminderSetting,
            account,
            txns,
          });
          if (!cacheBroke) {
            balancesByAccountId.set(account.id, createNewBalancesRecord(balMap[cacheKey], { account, isConnected }));
            // log.log('======================= CACHE HIT OR EMPTY TXNS FOR ', account.name);
            return;
          }

          const cacheObject = {
            accountId: account.id,
            acctReminderSetting,
            currentBalanceAsOf: account.currentBalanceAsOf,
            currentBalanceAsOfOn: account.currentBalanceAsOfOn,
            onlineBalance: account.onlineBalance,
            balanceAsOf: account.balanceAsOf,
            balanceAsOfOn: account.balanceAsOfOn,
            cpOnlineBalance: account.aggregators?.first?.()?.onlineBalance,
            cpOnlineBalanceAt: account.aggregators?.first?.()?.onlineBalanceAt,
            cpAvailableBalance: account.aggregators?.first?.()?.availableBalance,
          };
          const strictCacheObject = {
            ...cacheObject,
            txns,
          };

          const ocmCacheName = `acctBalances-${account.id}`;
          const cachedValues = getOptimalCacheFromObject(
            transactionsStore,
            ocmCacheName,
            cacheObject,
            strictCacheObject,
            (oldTxn, newTxn) => optimalCacheCompareFn(oldTxn, newTxn, account.id),
            (currentCache) => currentCache,
            2,
          );
          if (cachedValues) {
            // log.log('OPTIMAL CACHE USED =========================================', account.id);
            balancesByAccountId.set(account.id, cachedValues);
            return;
          }

          /*
           * FOR THIS ACCOUNT, the cache is broken or unavailable, so CALCULATIONS below
           */

          atLeastOneAccountBrokeCache = true;

          let endingBalance = 0;
          let endingBalanceNoRecurring = 0;
          let currentBalance = account.currentBalanceAsOf;
          let txnsDeltaFromCurrentBalance: number | null = null;
          let earliestTransactionDate = null;
          let finalBalance = 0;
          let excludePendingTxnTotal = 0;
          let exlcudeC2RTxnTotal = 0;

          let setCache = false;
          // just use defaults until transactions are available
          if (txns && !transactionsAreLoading && !accountsAreLoading) {

            setCache = true;

            currentBalance = 0;
            // if we have not already calculated the sum of transactions downloaded which match the accounts
            // currentBalance values, and the account has a currentBalance, use it to do the calc
            //
            const calculateTxnDelta =
              account.currentBalanceAsOf && (
                (!cacheRec) ||
                (cacheRec.txnsDeltaFromCurrentBalance === null) ||
                (account.currentBalanceAsOf && account.currentBalanceAsOf !== cacheRec.account.currentBalanceAsOf) ||
                (account.currentBalanceAsOfOn && account.currentBalanceAsOfOn !== cacheRec.account.currentBalanceAsOfOn)
              );

            log.log('======================= RECALCULATING FOR ', account.name, calculateTxnDelta, account.currentBalanceAsOf);
            /*
             * CALC METHOD 1 - 'R' Calculating the sum of original transactions associated with currentBalance
             */
            if (calculateTxnDelta || calculateTxnDelta === 0) {
              earliestTransactionDate = txns.size ? txns.first().postedOn : DateTime.local().toFormat('yyyy-MM-dd');

              endingBalance = account.currentBalanceAsOf;
              endingBalanceNoRecurring = account.currentBalanceAsOf;
              currentBalance = 0;
              txnsDeltaFromCurrentBalance = endingBalance;
              finalBalance = endingBalance;

              txns.forEach((txn) => {

                const txnDate = DateTime.fromISO(txn.postedOn);
                const currentBalanceAsOfOnDate = DateTime.fromISO(account.currentBalanceAsOfOn);

                if ((!txnDate as unknown as number) <= (currentBalanceAsOfOnDate as unknown as number)) {
                  finalBalance += Number(txn.amount);
                }

                if (includeTransactionInBalance(
                  txn,
                  {
                    recurringSetting: acctReminderSetting,
                    transactionsForAccountId: txns,
                    excludePendingTxn: undefined,
                    isPendingTxnDisabled: account.isBankPendingTxnsExcluded,
                  },
                )) {

                  const earliestDate = DateTime.fromISO(earliestTransactionDate!);

                  if (txnDate < earliestDate) {
                    earliestTransactionDate = txn.postedOn;
                  }
                  if (!isUnacceptedScheduledTxn(txn) && txnDate <= currentBalanceAsOfOnDate) {
                    // @ts-expect-error: 'txnsDeltaFromCurrentBalance' is possibly 'null'.
                    txnsDeltaFromCurrentBalance -= Number(txn.amount);
                  } else {
                    endingBalance += Number(txn.amount);
                    endingBalanceNoRecurring += (isUnacceptedScheduledTxn(txn) ? 0 : Number(txn.amount));
                  }
                  if (txnDate > DateTime.local().startOf('day')) {
                    currentBalance += Number(txn.amount);
                  }
                  if (excludePendingTxn && isBankOwnedPending(txn)) {
                    excludePendingTxnTotal += Number(txn.amount);
                  }
                  if (isC2RTransaction(txn)) {
                    exlcudeC2RTxnTotal += Number(txn.amount);
                  }
                }
              });
              endingBalance -= excludePendingTxnTotal + exlcudeC2RTxnTotal;
              currentBalance = endingBalance - currentBalance;
              assert(typeof currentBalance === 'number', 'currentBalance is not a number');
            } else {
              /*
               * CALC METHOD 2 and 3 - Calculating balance starting with either balanceAsOf or txnsDeltaFromCurrentBalance
               */
              // if we have a valid sum of original transactions in the cache, use it
              const useTxnDeltaAmount = balMap[cacheKey] && balMap[cacheKey].txnsDeltaFromCurrentBalance !== null;

              log.log('======================= CALC METHOD 2 FOR ', account.name, useTxnDeltaAmount, balMap[cacheKey] && balMap[cacheKey].txnsDeltaFromCurrentBalance);

              earliestTransactionDate = txns.size ? txns.first().postedOn : DateTime.local().toFormat('yyyy-MM-dd');

              endingBalance = useTxnDeltaAmount ? balMap[cacheKey].txnsDeltaFromCurrentBalance : Number(account.balanceAsOf);
              txnsDeltaFromCurrentBalance = useTxnDeltaAmount ? balMap[cacheKey].txnsDeltaFromCurrentBalance : null;
              currentBalance = endingBalance;
              finalBalance = endingBalance;
              endingBalanceNoRecurring = endingBalance;

              txns.forEach((txn) => {

                const txnDate = DateTime.fromISO(txn.postedOn);
                const balanceAsOfOnDate = DateTime.fromISO(account.balanceAsOfOn);

                if (useTxnDeltaAmount || (!account.balanceAsOfOn || txnDate > balanceAsOfOnDate)) {
                  finalBalance += Number(txn.amount);
                }

                if (includeTransactionInBalance(
                  txn,
                  {
                    recurringSetting: acctReminderSetting,
                    transactionsForAccountId: txns,
                    excludePendingTxn,
                    isPendingTxnDisabled: account.isBankPendingTxnsExcluded,
                  },
                )) {
                  const earliestDate = DateTime.fromISO(earliestTransactionDate!);
                  if (txnDate < earliestDate) {
                    earliestTransactionDate = txn.postedOn;
                  }
                  // if we are using the 'R' value, date does not matter, but if we are calculating from balanceAsOfOn, we
                  // only count transactions that are after the balanceAsOfOn date
                  if (useTxnDeltaAmount || (!account.balanceAsOfOn || txnDate > balanceAsOfOnDate)) {
                    endingBalance += Number(txn.amount);
                    endingBalanceNoRecurring += (isUnacceptedScheduledTxn(txn) ? 0 : Number(txn.amount));
                    if (txnDate <= DateTime.local().startOf('day')) {
                      currentBalance += Number(txn.amount);
                    }
                  }
                }
              });
            }
          }

          endingBalance = balRound(endingBalance);
          endingBalanceNoRecurring = balRound(endingBalanceNoRecurring);
          currentBalance = balRound(currentBalance);
          finalBalance = balRound(finalBalance);

          const balancesForAccountId = new AccountBalances({
            accountId: account.id,
            accountType: account.type,
            onlineBalance: !isConnected ? currentBalance : account.onlineBalance,
            endingBalance,
            endingBalanceNoRecurring,
            currentBalance,
            finalBalance,
            earliestTransactionDate,
            txnsDeltaFromCurrentBalance,  // 'R'
            cpOnlineBalance: account.aggregators?.first?.()?.onlineBalance,
            cpAvailableBalance: account.aggregators?.first?.()?.availableBalance,
          });
          balancesByAccountId.set(account.id, balancesForAccountId);

          // Set Cache
          if (setCache) {
            balMap[cacheKey] = {
              endingBalance,
              endingBalanceNoRecurring,
              currentBalance,
              finalBalance,
              txns,
              account,
              earliestTransactionDate,
              txnsDeltaFromCurrentBalance,
              acctReminderSetting,
              cpOnlineBalance: account.aggregators?.first?.()?.onlineBalance,
              cpAvailableBalance: account.aggregators?.first?.()?.availableBalance,
            };
            // Set the optimal Cache
            setOptimalCacheFromObject(transactionsStore, ocmCacheName, cacheObject, strictCacheObject, balancesForAccountId, 2);
          }
        });
      });

      const balanceMapToReturn = atLeastOneAccountBrokeCache ? retVal : overallMapCache || retVal;
      overallMapCache = balanceMapToReturn;
      return balanceMapToReturn;
    });
  })()),
);


// CMP-8174
// Running balance is balance today including due or overdue reminders if reminder prefrence is on for account
// For to calculate balace correctly, do the two steps
// case 1: for past dates, running balance is subtracted with amount of txns  and overdue schTxn instances between
// today and date for which balance is to be calculated
// case 2: for future dates, add amounts of due or future due schTxn and future txns till the date for
// which balance is to be calculated
const calcBalanceForAccountAtDate = (
  accountId,
  date,
  txnsByAccountId,
  balancesByAccountId,
  balanceStr,
  runningBalanceExcludeAccountIds,
) => {
  const balanceObject = balancesByAccountId.get(accountId);
  if (balanceObject) {
    let runningBalance = balanceObject?.[balanceStr];
    const txns = txnsByAccountId.get(accountId);
    if (txns) {
      const today = DateTime.local().toISODate();
      const balMap = getBalMap();
      const cacheRec = balMap[accountId];
      const excludePendingTxn = runningBalanceExcludeAccountIds?.includes(accountId);

      const account = accountsSelectors.getAccountById(store?.getState(), accountId);
      const parsedDate = DateTime.fromISO(date);
      const currentDate = DateTime.fromISO(today!);

      if (!parsedDate.hasSame(currentDate, 'day')) {
        txns.forEach((txn) => {
          if (!includeTransactionInBalance(
            txn,
            {
              recurringSetting: cacheRec?.acctReminderSetting,
              transactionsForAccountId: txnsByAccountId,
              excludePendingTxn,
              isPendingTxnDisabled: account?.isBankPendingTxnsExcluded,
            },
          )) {
            return;
          }
          
          const txnPostedDate = DateTime.fromISO(txn.postedOn).toFormat('yyyy-MM-dd');
          const txnPostedDateLuxon = DateTime.fromISO(txnPostedDate);
          if (parsedDate > currentDate) {
            if (txnPostedDateLuxon > currentDate && txnPostedDateLuxon <= parsedDate) {
              runningBalance += Number(txn.amount);
            }
          } else if (txnPostedDateLuxon <= currentDate && txnPostedDateLuxon > parsedDate) {
            runningBalance -= Number(txn.amount);
          }
        });
      }
    }
    return runningBalance?.toFixed(2);
  }
  return null;
};

export const getBalanceForAccountAtDate = createSelector(
  (state, props) => props.accountId,
  (state, props) => props.date,
  (state, props) => props.balanceType || 'endingBalance',
  getTransactionsByAccountId,
  getBalancesByAccountId,
  getRunningBalanceExcludeAccountIds,
  (accountId, date, balanceType, txnsByAccountId, balancesByAccountId, runningBalanceExcludeAccountIds) =>
    calcBalanceForAccountAtDate(
      accountId,
      date,
      txnsByAccountId,
      balancesByAccountId,
      balanceType,
      runningBalanceExcludeAccountIds,
    ),
);

export const getBalancesForAccountsAtDates = createSelector(
  (state, props) => props.accountIds,
  (state, props) => props.dates,
  getTransactionsByAccountId,
  getBalancesByAccountId,
  accountsSelectors.getAccountsById,
  getRunningBalanceExcludeAccountIds,
  (accountIds, dates, txnsByAccountId, balancesByAccountId, accountsById, runningBalanceExcludeAccountIds) => {
    const currBalanceStr = accountIds.map((id) => balancesByAccountId.get(id)?.currentBalance);
    const endingBalanceStr = accountIds.map((id) => balancesByAccountId.get(id)?.endingBalance);
    const cacheStr =
      accountIds &&
      dates &&
      `${accountIds.toJS().toString()}-${currBalanceStr.toJS().toString()}-${endingBalanceStr.toJS().toString()}-${dates[0]}-${dates[dates.length - 1]
      }`;
    return memoizeWithKey(cacheStr, () =>
      dates.reduce((dateBalances, date) => {
        let balanceTotal = 0;
        const accountsBalances = accountIds.reduce((acc, accountId) => {
          const account = accountsById.get(accountId);
          if (!account) return acc;
          if (account.type === 'INVESTMENT') return acc; // exclude investment accounts
          const balance = calcBalanceForAccountAtDate(
            accountId,
            date,
            txnsByAccountId,
            balancesByAccountId,
            'currentBalance',
            runningBalanceExcludeAccountIds,
          );
          balanceTotal = Number(parseFloat(balanceTotal as unknown as string) + parseFloat(balance ?? 0));
          return acc.set(accountId, { name: account.name, balance });
        }, OrderedMap());
        return dateBalances.set(date, { accountsBalances, total: balanceTotal });
      }, OrderedMap()));
  },
);

const investmentsBalMap = {};

const getInvestmentBalancesByAccountIdQuicken = createSelector(
  accountsSelectors.getAccountsById,
  getIHoldsByAccountId,
  (accounts, iHoldsByAccountId) => (
    (ImmutableMap()).withMutations((balancesByAccountId) => {
      accounts.forEach((account) => {

        if (account.type !== 'INVESTMENT') return;

        const holdings = iHoldsByAccountId?.get(account.id);

        // Try and use the cache. Holdings are just a one shot load unlike the other accounts so this doesn't get used.
        if (holdings && (investmentsBalMap[account.id] && investmentsBalMap[account.id].holdings === holdings)) {
          const endBal = investmentsBalMap[account.id] ? investmentsBalMap[account.id].endingBalance : 0;
          const currBal = investmentsBalMap[account.id] ? investmentsBalMap[account.id].currentBalance : 0;
          const eDate = investmentsBalMap[account.id] ? investmentsBalMap[account.id].earliestTransactionDate : null;
          balancesByAccountId.set(account.id, new AccountBalances({
            accountId: account.id,
            accountType: account.type,
            endingBalance: endBal,                // Total of everything
            onlineBalance: endBal,
            currentBalance: currBal,              // Today's balance
            earliestTransactionDate: eDate,
          }));
          // log.log('======================= CACHE HIT OR EMPTY HOLDINGS FOR ', account.name);
          return;
        }
        // log.log('======================= RECALCULATING HOLDING BALANCES FOR ', account.name);

        /*
          Loop over the investment holdings for this account and determine:
            1. The balanceAsOfOn from the latest modifiedAt date from each.
            2. The earliestTransactionDate from the earliest modifiedAt date from each.
            3. The endingBalance by summing latestPrice * numberOfShares + the account's cash balance
            4. The currentBalance = endingBalance
         */

        let balanceAsOfOn;
        let earliestTransactionDate: string | DateTime = DateTime.local();
        let endingBalance = 0.0;

        if (holdings) {
          holdings.forEach((holding) => {
            const modifiedAtDate = DateTime.fromISO(holding.modifiedAt).toFormat('MM-dd-yyyy');
            if (!balanceAsOfOn) balanceAsOfOn = modifiedAtDate;
            if (!earliestTransactionDate) earliestTransactionDate = modifiedAtDate;
            if ((modifiedAtDate as unknown as number) > (new Date(balanceAsOfOn) as unknown as number)) {
              balanceAsOfOn = modifiedAtDate;
            }
            // @ts-expect-error: Operator '<' cannot be applied to types 'string' and 'Date'.
            if (modifiedAtDate < new Date(earliestTransactionDate)) {
              earliestTransactionDate = modifiedAtDate;
            }
            endingBalance += (holding.numberOfShares * holding.latestPrice);
          });
        }

        // @ts-expect-error: Property 'cashBalance' does not exist on type 'RecordOf<AccountProps>'.
        endingBalance += account.cashBalance;

        const balancesForAccountId = new AccountBalances({
          accountId: account.id,
          accountType: account.type,
          currentBalance: endingBalance,
          onlineBalance: endingBalance,
          endingBalance,
          earliestTransactionDate,
        });

        balancesByAccountId.set(account.id, balancesForAccountId);
        investmentsBalMap[account.id] = {
          endingBalance,
          currentBalance: endingBalance,
          holdings,
          earliestTransactionDate,
        };
      });
    })
  ),
);

export const getAllBalancesByAccountId = createSelector(
  getBalancesByAccountId,
  getInvestmentBalancesByAccountIdQuicken,
  (balancesByAccountId, investmentBalancesByAccountId) => balancesByAccountId.merge(investmentBalancesByAccountId),
);

// add defaultBalance to the balances
export const getAllBalancesByAccountIdEx = createSelector(
  getAllBalancesByAccountId,
  (state) => getSharedPreferencesByPath(state, { group: 'dataset', path: ['shared', 'showBalance'] }) || ShowBalance.INCLUDE_PENDING,
  (state) => featureFlagsSelectors.getFeatureFlag(state, 'balanceToggle'),
  (balancesByAccountId, showBalance, balanceToggle) => balancesByAccountId.map((balances) => {
    let defaultBalance = balances.currentBalance;
    if (balanceToggle) {
      switch (showBalance) {
        case ShowBalance.INCLUDE_PENDING:
          defaultBalance = balances.currentBalance;
          break;
        case ShowBalance.BANK_REPORTED:
          defaultBalance = balances.cpOnlineBalance;
          break;
        default:
          verify(false, `unexpected showBalance value = ${showBalance}`);
      }
    }
    return balances.set('defaultBalance', defaultBalance);
  }),
);
